import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FiCopy, FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import GodAutoBotModal from '../components/GodAutoBotModal';
import { formatSingaporeCompactStamp, formatSingaporeTimeHm } from '../utils/singaporeTime';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'ngrok-skip-browser-warning': 'true',
});

function memberLabel(member) {
    if (!member) return '—';
    if (member.fullName) return member.fullName;
    const godID = member.godID || 'N/A';
    const godName = member.godName || 'N/A';
    const webID = member.webID || 'N/A';
    return `${godID} - ${godName} - ${webID}`;
}

function chatTitleOf(target) {
    const chat = target?.chat;
    if (!chat || typeof chat !== 'object') return '—';
    return chat.chatTitle || chat.chatUsername || chat.chatID || '—';
}

function groupsCell(targets = []) {
    if (!targets.length) return '—';
    return targets.map((t) => chatTitleOf(t)).join('\n');
}

function lastSentCell(targets = []) {
    const lines = (targets || [])
        .filter((t) => t.lastSentAt)
        .map((t) => `${chatTitleOf(t)}\n${formatSingaporeCompactStamp(t.lastSentAt)}`);
    return lines.length ? lines.join('\n\n') : '—';
}

function scheduleKey(target) {
    const schedule = target?.schedule || {};
    const days = [...(schedule.daysOfWeek || [])].map(Number).sort().join(',');
    const startMs = target?.recurringStartAt ? new Date(target.recurringStartAt).getTime() : '';
    const stopMs = target?.recurringStopAt ? new Date(target.recurringStopAt).getTime() : '';
    return [
        schedule.type || '',
        Number(schedule.interval) || 1,
        days,
        Number.isFinite(startMs) ? startMs : '',
        Number.isFinite(stopMs) ? stopMs : '',
        String(target?.openHour || ''),
        String(target?.closeHour || ''),
    ].join('|');
}

function allTargetsSameSchedule(targets = []) {
    if (targets.length <= 1) return true;
    const first = scheduleKey(targets[0]);
    return targets.every((t) => scheduleKey(t) === first);
}

function cloneDocForCreate(doc) {
    const name = String(doc?.autobotConfigName || '').trim();
    return {
        ...doc,
        _id: undefined,
        createdAt: undefined,
        updatedAt: undefined,
        __v: undefined,
        autobotConfigName: name ? `${name} (copy)` : '',
        targets: (doc?.targets || []).map((t) => ({
            ...t,
            _id: undefined,
            lastMessageIds: [],
            lastSentAt: null,
            lastRunAt: null,
            lastRunStatus: undefined,
            lastError: '',
            nextRunAt: null,
        })),
    };
}

const GodAutoBotPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [modal, setModal] = useState({ mode: null, doc: null });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [sortBy, setSortBy] = useState('');

    const fetchList = useCallback(async (page = 1, search = '', sort = '') => {
        try {
            setLoading(true);
            setError('');
            const params = new URLSearchParams({ page: String(page), limit: '20' });
            if (search) params.set('search', search);
            if (sort === 'startTime' || sort === '-startTime') params.set('sort', sort);
            const res = await axios.get(`${API_BASE}/api/god-autobots?${params.toString()}`, {
                headers: authHeaders(),
            });
            if (res.data.success) {
                setItems(res.data.data || []);
                setTotalPages(res.data.pagination?.pages || 1);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchList(currentPage, searchTerm, sortBy);
    }, [currentPage, searchTerm, sortBy, fetchList]);

    const openModal = async (mode, row) => {
        if (mode === 'create' && !row) {
            setModal({ mode, doc: null });
            return;
        }
        try {
            const res = await axios.get(`${API_BASE}/api/god-autobots/${row._id}`, { headers: authHeaders() });
            if (!res.data.success) return;
            if (mode === 'create') {
                setModal({ mode: 'create', doc: cloneDocForCreate(res.data.data) });
                return;
            }
            setModal({ mode, doc: res.data.data });
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await axios.delete(`${API_BASE}/api/god-autobots/${deleteTarget._id}`, { headers: authHeaders() });
            setDeleteTarget(null);
            fetchList(currentPage, searchTerm, sortBy);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">AutoBot</h1>
                    <button
                        type="button"
                        onClick={() => openModal('create')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <FiPlus /> Create
                    </button>
                </div>

                <form
                    className="mb-4 flex gap-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setCurrentPage(1);
                        setSearchTerm(searchInput.trim());
                    }}
                >
                    <div className="relative max-w-sm flex-1">
                        <FiSearch className="pointer-events-none absolute right-3 top-2.5 text-gray-400" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search name…"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-9"
                        />
                    </div>
                    <button type="submit" className="px-4 py-2 bg-gray-800 text-white rounded-md">
                        Search
                    </button>
                    <select
                        value={sortBy}
                        onChange={(e) => {
                            setSortBy(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white"
                        title="Sort by"
                    >
                        <option value="">Sort: Default</option>
                        <option value="startTime">Sort: Start Time (earliest)</option>
                        <option value="-startTime">Sort: Start Time (latest)</option>
                    </select>
                </form>

                {error ? <div className="mb-4 rounded-md bg-red-100 p-3 text-sm text-red-700">{error}</div> : null}

                <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">God Member</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Groups</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Sent</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading…</td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No AutoBot configs</td>
                                </tr>
                            ) : (
                                items.map((row) => (
                                    <tr key={row._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.autobotConfigName}</td>
                                        <td className="px-4 py-3 text-sm">{row.active ? '✅' : '❌'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-800">{memberLabel(row.godMember)}</td>
                                        <td className="px-4 py-3 text-sm text-gray-800 whitespace-pre-line">{groupsCell(row.targets)}</td>
                                        <td className="px-4 py-3 text-sm text-gray-800">
                                            {allTargetsSameSchedule(row.targets) ? (
                                                formatSingaporeTimeHm(row.targets?.[0]?.recurringStartAt)
                                            ) : (
                                                <span className="inline-flex items-center gap-2">
                                                    Different Timing
                                                    <button
                                                        type="button"
                                                        title="View"
                                                        className="text-gray-600 hover:text-gray-900"
                                                        onClick={() => openModal('view', row)}
                                                    >
                                                        <FiEye />
                                                    </button>
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-800 whitespace-pre-line">{lastSentCell(row.targets)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex gap-2">
                                                <button type="button" title="View" className="text-gray-600 hover:text-gray-900" onClick={() => openModal('view', row)}>
                                                    <FiEye />
                                                </button>
                                                <button type="button" title="Edit" className="text-blue-600 hover:text-blue-800" onClick={() => openModal('edit', row)}>
                                                    <FiEdit2 />
                                                </button>
                                                <button type="button" title="Duplicate" className="text-emerald-600 hover:text-emerald-800" onClick={() => openModal('create', row)}>
                                                    <FiCopy />
                                                </button>
                                                <button type="button" title="Delete" className="text-red-600 hover:text-red-800" onClick={() => setDeleteTarget(row)}>
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 ? (
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                ) : null}
            </div>

            {modal.mode ? (
                <GodAutoBotModal
                    mode={modal.mode}
                    initialDoc={modal.doc}
                    onClose={() => setModal({ mode: null, doc: null })}
                    onSaved={() => {
                        setModal({ mode: null, doc: null });
                        fetchList(currentPage, searchTerm, sortBy);
                    }}
                />
            ) : null}

            {deleteTarget ? (
                <DeleteConfirmationModal
                    title="Delete AutoBot config"
                    message="This config will be moved to trash."
                    itemName={deleteTarget.autobotConfigName}
                    isLoading={deleteLoading}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            ) : null}
        </Layout>
    );
};

export default GodAutoBotPage;
